/**
 * The Radar strip above a work order's details: whether Radar is on this job,
 * what it is doing, and the two ways to intervene.
 *
 * It reads the newest run on the work order. A work order can be reopened and
 * picked up again, so there can be several; the older ones stay on the Radar
 * Activity tab, and this says what is true now — including after the run has
 * ended, because "Radar handled this" is as worth knowing as "Radar is on it".
 *
 * Pause and Cancel are relayed to Radar rather than stored, so a click changes
 * nothing here until Radar's next event lands. The banner refetches rather than
 * assuming; if Radar cannot be reached the service says so and the run is left
 * exactly as it was.
 */

import { Alert, Button, Card, Popconfirm, message } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import useMountEffect from '../../hooks/useMountEffect';
import { isRadarEnabled } from '../../utils/FeatureFlagUtils';
import OscarMark from '../../ai_agent/components/shared/OscarMark';
import { withUserType } from '../withUserType';
import { getEntityById, getWorkOrderTimeZoneId, nullSafeGet } from '../../utils/DataAccessUtils';
import { AOPExecution, AOPOverrideAction, isExecutionPaused } from '../../types/AOPExecutionTypes';
import {
	describeOverrideError,
	overrideExecution,
	readNewestWorkOrderExecution,
} from '../../thunks/agent_executions_thunks';
import { AOP_RUN_STATES, runStateOf } from '../aop_builder/utils/AOPExecutionStates';
import { describeEvent } from '../aop_builder/utils/AOPExecutionUtils';
import AOPEventSentence from '../aop_builder/AOPEventSentence';

const MARK_SIZE = 20;

/**
 * How long to keep asking whether an override took.
 *
 * The service relays an override to Radar and stores nothing, so a 202 means
 * "sent", not "done" — the run only changes when Radar's next event lands. One
 * refetch straight after the 202 is always too early, which is why Pause never
 * became Resume. So the run is read a few times, and if it still has not
 * changed the banner shows whatever the run actually says rather than what was
 * asked for.
 *
 * Nothing guards this against unmounting. React 18 makes a state write on a
 * gone component a no-op, so the only cost of navigating away mid-wait is the
 * few reads still in flight — which is a smaller price than a ref that has to
 * be kept correct through StrictMode's double-invoked effects.
 */
const SETTLE_ATTEMPTS = 6;
const SETTLE_DELAY_MS = 1500;

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Whether a run has caught up with the override that was sent for it. */
const reflects = (execution: AOPExecution | null, action: AOPOverrideAction): boolean => {
	if (!execution) {
		return false;
	}
	if (action === 'cancel') {
		return execution.state === 'finished';
	}
	return isExecutionPaused(execution) === (action === 'pause');
};

/** A moment in the site's zone when one is known, the viewer's when it is not. */
const momentForSite = (value: string, timeZoneId?: string) =>
	timeZoneId ? moment.tz(value, timeZoneId) : moment(value);

/**
 * What the run is doing, as the one line the banner has room for.
 *
 * A live run is described by its newest event; a finished one by when it ended
 * and why, which is the thing somebody arriving late wants.
 */
/**
 * The run in a line, as JSX rather than a string.
 *
 * It was a string, which meant the person who paused a run could only ever be
 * their address — a sentence cannot hold a component. See `AOPEventSentence`.
 */
const summaryOf = (execution: AOPExecution, timeZoneId: string): React.ReactNode => {
	const latest = execution.latest;

	if (execution.state === 'finished') {
		const ended = execution.finished_at
			? `Finished at ${momentForSite(execution.finished_at, timeZoneId).format('h:mm A z')}`
			: 'Finished';
		const reason = latest && latest.snapshot ? latest.snapshot.reason : null;
		return reason ? `${ended} · ${reason}` : ended;
	}

	if (!latest) {
		return 'Starting';
	}

	const described = describeEvent(latest, timeZoneId);
	const waitingUntil = latest.snapshot ? latest.snapshot.waiting_until : null;
	/*
	  The countdown is the part that makes a wait legible — "18 min left" says
	  more than the hour it runs out at.

	  Which way round has to be asked, though. `fromNow(true)` is the suffixless
	  form, so it answers "18 minutes" whether that is eighteen to go or eighteen
	  past, and appending "left" to the second read as time remaining on a wait
	  that had already run out.
	*/
	const until = waitingUntil ? moment(waitingUntil) : null;
	const left = until
		? `${until.fromNow(true)} ${until.isAfter(moment()) ? 'left' : 'overdue'}`
		: null;

	return (
		<>
			<AOPEventSentence described={described} />
			{left ? ` ${left}` : ''}
		</>
	);
};

const RadarWorkOrderBanner: FC<any> = ({
	workOrderId,
	timeZoneId,
	userType,
	execution,
	loadExecution,
	sendOverride,
}) => {
	// Which override is still being waited on, so the buttons can say so.
	const [pending, setPending] = useState<AOPOverrideAction | null>(null);
	/** A refused override, inside the card. Never about reading the run. */
	const [error, setError] = useState<string | null>(null);

	/*
	  `force` on every read but the first: the mount read may be joined to the
	  one the tab gate started, which is what makes them a single request, while
	  a read taken after an override has to actually go and ask again.
	*/
	const load = useCallback(
		(force = true): Promise<AOPExecution | null> => loadExecution(workOrderId, force),
		[loadExecution, workOrderId]
	);

	/*
	  Nothing is said while this is in flight, and nothing if it fails.
	
	  The card is additive — it appears when Radar is on a work order and is
	  absent otherwise — so every work order in the company was carrying a
	  spinner, and then a red panel on a bad read, about an agent most of them
	  have never been near. A run it cannot see is one it cannot report, and the
	  page reads exactly as it does for a work order Radar has never touched.
	
	  The cost is accepted: a run that is live but unreadable loses its card
	  until the page is opened again. The Radar Activity tab is where a failure
	  worth reporting is reported.
	*/
	useMountEffect(() => {
		load(false).catch(() => undefined);
	});

	/**
	 * Waits for the run to catch up, rather than deciding the answer here.
	 *
	 * Nothing is written locally: the banner only ever shows what the run says,
	 * so a relay that never reaches Radar leaves the card honest instead of
	 * showing a pause that did not happen.
	 */
	const settle = useCallback(
		(action: AOPOverrideAction): Promise<void> => {
			const attempt = (left: number): Promise<void> =>
				wait(SETTLE_DELAY_MS)
					/*
					  A read that fails is not an answer, and it is not a refused
					  override either. It used to reject into `onOverride`'s catch and
					  say the relay had not reached Radar, directly under the toast
					  saying it had. It counts as "not caught up yet": the next attempt
					  asks again, and if none of them land the card simply stays as it
					  was, which is what it does when a run is slow to reflect anyway.
					*/
					.then(() => load().catch(() => null))
					.then((latest) =>
						reflects(latest, action) || left <= 1 ? undefined : attempt(left - 1)
					);

			return attempt(SETTLE_ATTEMPTS);
		},
		[load]
	);

	const onOverride = useCallback(
		(action: AOPOverrideAction) => {
			if (!execution) {
				return;
			}

			setPending(action);
			setError(null);
			sendOverride(execution.id, action, null)
				.then(() => {
					message.success('Sent to Radar.');
					return settle(action);
				})
				.catch((code) => {
					setError(describeOverrideError(code));

					/*
					  `execution_not_running` is the card being out of date rather than
					  the override failing: the run ended between this card loading and
					  the click. Saying so while still showing it as active, with the
					  same controls offered, leaves the reader to reconcile two claims
					  that cannot both be true — so the run is read again and the card
					  catches up.
					*/
					if (code === 'execution_not_running') {
						load().catch(() => undefined);
					}
				})
				.then(() => setPending(null));
		},
		[execution, load, sendOverride, settle]
	);

	// Radar has never touched this work order, so there is nothing to say about
	// it. Every work order in the company would otherwise carry an empty strip.
	if (!execution) {
		return null;
	}

	const chrome = AOP_RUN_STATES[runStateOf(execution)];
	// Not `state === 'running'`: a held run is `paused` now, and Resume and
	// Cancel are exactly what it needs offering. Anything not finished is a run
	// somebody can still act on.
	const isRunning = execution.state !== 'finished';
	const isPaused = isExecutionPaused(execution);
	// The run carries it. This used to ask `GET /scenarios` for the procedure by
	// id, which meant waiting for a run to say which procedure it was before the
	// name could even be requested.
	const procedureName = execution.procedure_name;

	return (
		/*
		  The gap goes on a wrapper, not on the card: antd's stylesheet loads after
		  Tailwind's, so `.ant-card`'s own margin beats a utility of the same
		  specificity on source order alone. A plain div has nothing to argue with.

		  The card itself is the one the rest of this column is built from, so Radar
		  reads as part of the work order rather than as a notice stuck on top of
		  it — tinted by the run's state, which is the one thing it has to say at a
		  glance. That fill does need `!`, since it is fighting `.ant-card` directly.
		*/
		<div className="mb-m">
			<Card className={chrome.bgClass}>
				{/* 16px, so the controls read as a row under the sentence, not with it. */}
				<div className="flex flex-col gap-m">
					<div className="flex flex-wrap items-center gap-xs">
						<span className={chrome.textClass}>
							<OscarMark size={MARK_SIZE} animation={chrome.isAnimated ? 'flap' : undefined} />
						</span>
						<span className={`bodymedium ${chrome.textClass}`}>{chrome.label}</span>
						<span className="bodymedium text-character-primary">
							{procedureName ? procedureName : 'Radar'}:
						</span>
						<span className="bodyregular min-w-0 text-character-secondary">
							{summaryOf(execution, timeZoneId)}
						</span>
					</div>

					{error ? <Alert type="error" showIcon message={error} /> : null}

					<div className="flex flex-wrap items-center gap-xs">
						{/*
						  Only a running run can be held or ended. A finished one keeps the
						  card — what Radar did is worth saying — but has nothing to act on.
						*/}
						{isRunning ? (
							<Button
								loading={pending === 'pause' || pending === 'resume'}
								disabled={pending !== null}
								onClick={() => onOverride(isPaused ? 'resume' : 'pause')}
							>
								{isPaused ? 'Resume' : 'Pause'}
							</Button>
						) : null}
						{isRunning ? (
							<Popconfirm
								title="Cancel this run? Radar stops here and does not pick the work order up again."
								okText="Cancel run"
								okButtonProps={{ danger: true }}
								cancelText="Leave it running"
								onConfirm={() => onOverride('cancel')}
							>
								<Button loading={pending === 'cancel'} disabled={pending !== null}>
									Cancel
								</Button>
							</Popconfirm>
						) : null}
						<Link to={`/${userType}/workOrders/detail/${workOrderId}/radarActivity`}>
							<Button>View activity</Button>
						</Link>
					</div>
				</div>
			</Card>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	/*
	  The run the work-order page has already read. `WorkOrdersDetailPage` asks
	  the same question to decide whether the "Radar Activity" tab exists, so
	  both read one answer out of the store instead of each calling the service.

	  Bracket access, not a `nullSafeGet` path, because the key is a dynamic id.
	*/
	execution: state.agent_executions.newestByWorkOrder[nullSafeGet('workOrderId', ownProps)],
	/*
	  The site's zone, as the Radar Activity page reads it. Events are stamped
	  UTC and the times on this card are absolute, so without it the card reads
	  in the viewer's zone while the timeline it links to reads in the site's.
	*/
	timeZoneId: getWorkOrderTimeZoneId(
		getEntityById(nullSafeGet('workOrderId', ownProps), state.work_orders, null)
	),
});

const mapDispatchToProps = (dispatch) => ({
	loadExecution: (workOrderId: number, force: boolean) =>
		dispatch(readNewestWorkOrderExecution(workOrderId, force)),
	sendOverride: (executionId: string, action: AOPOverrideAction, reason: string | null) =>
		dispatch(overrideExecution(executionId, action, reason)),
});

const ConnectedRadarWorkOrderBanner = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(RadarWorkOrderBanner)
);

/** What a caller passes. The rest arrives from `connect` and `withUserType`. */
export interface RadarWorkOrderBannerProps {
	workOrderId: number;
}

/**
 * Nothing at all until Radar is on for this reader.
 *
 * A wrapper rather than an early return inside the banner, because the read is
 * a mount effect: a hook cannot be skipped, so the only way to not ask the
 * agent service is to not mount the thing that asks. Every buyer opens work
 * orders and most will never have Radar — this is the difference between a
 * request per work-order view for the whole company and none.
 */
const GatedRadarWorkOrderBanner: FC<RadarWorkOrderBannerProps> = (props) =>
	isRadarEnabled() ? <ConnectedRadarWorkOrderBanner {...props} /> : null;

export default GatedRadarWorkOrderBanner;
